function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

/**
 * Updates labels of all active discount badges based on window.promoConfig.
 * This is defined globally to be accessible by cart.js, cart-drawer.js, and other components.
 */
window.updateDiscountLabels = () => {
  const config = window.promoConfig || [];
  const gwpConfig = window.gwpConfig || [];
  const badges = document.querySelectorAll("[data-discount-title]");

  // Use a small delay to ensure any dynamic content is fully in the DOM
  if (badges.length > 0) {
    console.log(`[Promo] Found ${badges.length} badges to update. Config:`, config);
  }

  badges.forEach(badge => {
    const title = badge.dataset.discountTitle;
    if (!title) return;

    const cleanTitle = title.trim().toLowerCase();

    // 1. Check if this discount is a GWP
    const matchingGwp = gwpConfig.find(g => g.code && g.code.trim().toLowerCase() === cleanTitle);
    if (matchingGwp) {
      // Hide the redundant cart-level "promo-success-badge" because GWP has its own rich badge
      if (badge.classList.contains("promo-success-badge")) {
        badge.style.display = "none";
        return;
      }

      // For line-item discount badges (product-promotions-container items),
      // hide them if they appear on a product that is NOT the gift product.
      if (badge.classList.contains("icon-block")) {
        const cartItemRow = badge.closest(".cart-item");
        if (cartItemRow) {
          const itemVariantId = cartItemRow.dataset.variant;
          if (String(itemVariantId) !== String(matchingGwp.variantId)) {
            badge.style.display = "none";
            return;
          }
        }
      }
    }

    // Ensure it's visible if it was previously hidden
    badge.style.display = "";

    const matchingConfig = config.find(c => c.code && c.code.trim().toLowerCase() === cleanTitle);
    const finalLabel = (matchingConfig && matchingConfig.label)
      ? matchingConfig.label
      : (matchingGwp && matchingGwp.label) ? matchingGwp.label : null;

    if (finalLabel) {
      const labelSpan = badge.querySelector(".promo-item-label") || badge.querySelector("span.font-bold");
      if (labelSpan) {
        labelSpan.textContent = finalLabel;
      }
    } else {
      const labelSpan = badge.querySelector(".promo-item-label") || badge.querySelector("span.font-bold");
      if (labelSpan) {
        // Keep the original title if no label is found (ensures no extra text)
        labelSpan.textContent = title;
      }
    }
  });
};

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min),
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelector(".menu-drawer__close-button")?.addEventListener(
      "click",
      (event) => {
        this.closeMenuDrawer(event, event.currentTarget);
      },
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(#NewsletterPopupButton--header-drawer)",
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary"),
      )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus,
          );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
document.querySelectorAll(".search-modal__close-button").forEach((button) => {
  button.addEventListener("click", function (event) {
    event.preventDefault();

    const details = this.closest("details");
    if (details) {
      details.removeAttribute("open");
    }

    // Restore scrolling
    document.body.style.overflow = "";
    document.body.classList.remove("overflow-hidden");
  });
});
customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
        this.closest(".header-wrapper").classList.contains(
          "header-wrapper--border-bottom",
        )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset,
      )}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset,
        )}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

// class ModalDialog extends HTMLElement {
//   constructor() {
//     super();
//     this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
//     this.addEventListener('keyup', (event) => {
//       if (event.code.toUpperCase() === 'ESCAPE') this.hide();
//     });
//     if (this.classList.contains('media-modal')) {
//       this.addEventListener('pointerup', (event) => {
//         if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
//       });
//     } else {
//       this.addEventListener('click', (event) => {
//         if (event.target === this) this.hide();
//       });
//     }
//   }

//   connectedCallback() {
//     if (this.moved) return;
//     this.moved = true;
//     this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
//     document.body.appendChild(this);
//   }

//   show(opener) {
//     this.openedBy = opener;
//     const popup = this.querySelector('.template-popup');
//     document.body.classList.add('overflow-hidden');
//     this.setAttribute('open', '');
//     if (popup) popup.loadContent();
//     trapFocus(this, this.querySelector('[role="dialog"]'));
//     window.pauseAllMedia();
//   }

//   hide() {
//     document.body.classList.remove('overflow-hidden');
//     document.body.dispatchEvent(new CustomEvent('modalClosed'));
//     this.removeAttribute('open');
//     removeTrapFocus(this.openedBy);
//     window.pauseAllMedia();
//   }
// }
// customElements.define('modal-dialog', ModalDialog);
class ModalDialog extends HTMLElement {
  static openCount = 0;

  constructor() {
    super();
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) {
      closeButton.addEventListener("click", this.hide.bind(this, false));
    }
    this.addEventListener("keyup", (event) => {
      if (event.code && event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    const section = this.closest(".shopify-section");
    if (section) {
      this.dataset.section = section.id.replace("shopify-section-", "");
    }
    document.body.appendChild(this);
  }

  static shouldLockScroll() {
    const openModals = document.querySelectorAll('modal-dialog[open]');
    // Lock if ANY open modal does NOT have data-allow-scroll (i.e., requires lock)
    return Array.from(openModals).some(m => !m.hasAttribute('data-allow-scroll'));
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");

    if (!this.hasAttribute('data-stack')) {
      // Close others only if not stackable
      const openModals = document.querySelectorAll("modal-dialog[open]");
      openModals.forEach(openModal => {
        if (openModal !== this) {
          openModal.classList.add("hidden");
          openModal.removeAttribute("open");
        }
      });
    }

    this.setAttribute("open", "");
    this.classList.remove("hidden");

    ModalDialog.openCount++;
    // Conditional lock based on all open modals
    if (ModalDialog.openCount > 0 && ModalDialog.shouldLockScroll()) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // Auto z-index for stacking (higher = on top)
    this.style.zIndex = 1000 + ModalDialog.openCount;

    if (popup?.loadContent) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    window.dispatchEvent(new CustomEvent("modaldialog:opened", { detail: { enabled: true, eventTriggered: this } }));
  }

  hide() {
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    this.classList.add("hidden");

    ModalDialog.openCount--;
    // Conditional unlock based on remaining open modals
    if (ModalDialog.openCount <= 0) {
      document.body.classList.remove("overflow-hidden");
    } else if (!ModalDialog.shouldLockScroll()) {
      document.body.classList.remove("overflow-hidden");
    }

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

window.addEventListener("modaldialog:opened", (e) => {
  if (
    !e?.detail?.eventTriggered?.id ||
    e?.detail?.eventTriggered?.id != "MapClubLoginModal"
  )
    return;
  // fetch('https://d-jarvis-partner.blubox.id/api/auth/token', {
  //   method: 'POST',
  //   headers: {
  //     'Content-Type': 'application/json',
  //     'x-api-key': 'AFYj9n9vYFf3LMMS77gSC_l-7dIOVXf0s_vyVxIcEWU'
  //   },
  //   body: JSON.stringify({})
  // })
  // .then(res => res.json())
  // .then(data => console.log(data))
  // .catch(err => console.error(err));
  const input = e?.detail?.eventTriggered.querySelector('input[type="tel"]');
  const errorMsg = e?.detail?.eventTriggered?.querySelector("#mobile-error");
  const wrapper = e?.detail?.eventTriggered?.querySelector("#mobile-wrapper");

  function validateMobile(value) {
    const digitOnly = value.replace(/\D/g, ""); // keep only digits

    if (digitOnly.length < 8 || digitOnly.length > 13) {
      return {
        valid: false,
        message: "Please input mobile number with 8 to 13 digits",
      };
    }

    if (!/^8/.test(digitOnly)) {
      return { valid: false, message: "Invalid phone number" };
    }

    return { valid: true, message: "" };
  }

  input.addEventListener("input", function () {
    const value = input.value.trim();
    const { valid, message } = validateMobile(value);

    if (!valid) {
      errorMsg.textContent = message;
      errorMsg.classList.remove("hidden");
      wrapper.classList.add("border-red-500");
      wrapper.classList.remove("border-gray-300");
    } else {
      errorMsg.textContent = "";
      errorMsg.classList.add("hidden");
      wrapper.classList.remove("border-red-500");
      wrapper.classList.add("border-gray-300");
    }
  });
});
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container",
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const button = this.querySelector("button");

    if (!button) return;

    button.addEventListener("click", () => {
      const modalId = this.getAttribute("data-modal");
      const modal = document.querySelector(modalId);
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe"),
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
      this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active",
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true,
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    console.log('[DEBUG VariantSelects] connectedCallback FIRED, id:', this.id);
    this.addEventListener("change", (event) => {
      console.log('[DEBUG VariantSelects] change event on:', event.target.tagName, event.target.type, 'name:', event.target.name, 'value:', event.target.value);
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      const collectedValues = this.selectedOptionValues;
      const checkedInputs = Array.from(this.querySelectorAll("fieldset input:checked"));
      console.log('[DEBUG VariantSelects] PUBLISHING option:change', {
        selectedOptionValues: collectedValues,
        checkedInputsDetail: checkedInputs.map(inp => ({ name: inp.name, value: inp.value, optionValueId: inp.dataset.optionValueId, checked: inp.checked }))
      });
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: collectedValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    console.log('[DEBUG updateSelectionMetadata] FIRED', {
      tagName,
      type: target.type,
      value,
      name: target.name,
      checked: target.checked,
      id: target.id,
    });

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const fieldset = target.closest(`.product-form__input`);
      console.log('[DEBUG updateSelectionMetadata] RADIO branch', {
        fieldsetFound: !!fieldset,
        fieldsetClasses: fieldset ? fieldset.className : 'N/A',
      });
      if (!fieldset) return;

      const selectedSwatchValue = fieldset.querySelector("[data-selected-value]");
      console.log('[DEBUG updateSelectionMetadata] [data-selected-value] element:', selectedSwatchValue);
      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = value;
        console.log('[DEBUG updateSelectionMetadata] Updated via [data-selected-value] to:', value);
        return;
      }

      const labelSpan = fieldset.querySelector('legend [id^="selected-"]');
      console.log('[DEBUG updateSelectionMetadata] labelSpan search:', {
        found: !!labelSpan,
        spanId: labelSpan ? labelSpan.id : 'N/A',
        currentText: labelSpan ? labelSpan.textContent : 'N/A',
      });

      if (labelSpan) {
        const inputName = (target.name || "").trim().toLowerCase();
        const isColor =
          inputName.startsWith("color-") || inputName.startsWith("warna-");
        let displayVal = value;
        if (isColor) {
          displayVal = value
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/-/g, " ")
            .split("ddpattern")[0]
            .trim()
            .split("pattern")[0]
            .trim()
            .replace(/ dd/g, "")
            .trim()
            .replace(/[0-9]/g, "")
            .trim()
            .split(/\s+/)
            .filter((w) => w)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
        }
        console.log('[DEBUG updateSelectionMetadata] SETTING label:', {
          inputName,
          isColor,
          rawValue: value,
          displayVal,
          spanId: labelSpan.id,
        });
        labelSpan.textContent = " " + displayVal;
        labelSpan.style.display = "inline";
      } else {
        console.log('[DEBUG updateSelectionMetadata] NO labelSpan found! fieldset HTML:', fieldset.querySelector('legend')?.innerHTML);
      }
    } else {
      console.log('[DEBUG updateSelectionMetadata] SKIPPED - not SELECT or radio INPUT');
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

/* High contrast JS */
class HighContrastToggle extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="checkbox"]');
    this.syncToggles = this.syncToggles.bind(this);
    this.onToggle = this.onToggle.bind(this);
  }

  connectedCallback() {
    // Sync state from body on load
    const isActive = document.body.hasAttribute("data-high-contrast");
    if (this.input) {
      this.input.checked = isActive;
      this.input.addEventListener("change", this.onToggle);
    }

    const isHigh = localStorage.getItem("high-contrast");

    if (isHigh == "true") {
      document.body.setAttribute("data-contrast", "high");
      if (this.input) {
        this.input.checked = true;
        this.input.addEventListener("change", this.onToggle);
      }
    }
    if (isHigh == true) {
      console.log("isHight", isHigh);
      document.body.setAttribute("data-contrast", "high");
    }
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener("change", this.onToggle);
    }
  }

  onToggle(e) {
    const isChecked = e.target.checked;

    // Set or remove data attribute on <body>
    if (isChecked) {
      document.body.setAttribute("data-contrast", "high");
      localStorage.setItem("high-contrast", "true");
    } else {
      document.body.removeAttribute("data-contrast");
      localStorage.setItem("high-contrast", "false");
    }

    // Dispatch event to sync other toggles
    window.dispatchEvent(
      new CustomEvent("highcontrast:toggled", {
        detail: { enabled: isChecked },
      }),
    );
  }

  syncToggles(e) {
    if (this.input && this.input.checked !== e.detail.enabled) {
      this.input.checked = e.detail.enabled;
    }
  }
}

// Register the custom element
customElements.define("high-contrast-toggle", HighContrastToggle);

// Sync other instances globally
window.addEventListener("highcontrast:toggled", (e) => {
  document.querySelectorAll("high-contrast-toggle").forEach((el) => {
    el.syncToggles?.(e);
  });
});

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".tooltip-container").forEach((el) => {
    el.addEventListener("click", (event) => {
      event.currentTarget.classList.toggle("tooltip-container--active");
    });
  });
});

// Thumbnail in Quick Add
document.addEventListener("mousemove", function (e) {
  if (!(e.target instanceof Element)) return;

  const wrapper = e.target.closest(".quick-add-modal .product__media");
  if (!wrapper) return;

  const img = wrapper.querySelector("img");
  if (!img) return;

  const rect = wrapper.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const xPercent = (x / rect.width) * 100;
  const yPercent = (y / rect.height) * 100;

  img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
  img.style.transform = "scale(2.5)";
  img.style.cursor = "zoom-in";
});

document.addEventListener(
  "mouseleave",
  function (e) {
    if (!(e.target instanceof Element)) return;

    const wrapper = e.target.closest(".quick-add-modal .product__media");
    if (!wrapper) return;

    const img = wrapper.querySelector("img");
    if (img) {
      img.style.transform = "scale(1)";
      img.style.transformOrigin = "center center";
      img.style.cursor = "zoom-in";
    }
  },
  true,
);
// Gift receipt toggle
document.addEventListener("DOMContentLoaded", function () {
  const checkbox = document.getElementById("gift-receipt-checkbox");
  const giftMessage = document.getElementById("gift-message-container");
  const giftMessageInput = document.getElementById("gift-message");
  const charCounter = document.getElementById("GiftMessageCharCounter");

  if (!checkbox || !giftMessage || !giftMessageInput || !charCounter) return;

  if (checkbox.checked) {
    giftMessage.classList.remove("hidden");
  }

  checkbox.addEventListener("change", function () {
    if (this.checked) {
      giftMessage.classList.remove("hidden");
    } else {
      giftMessage.classList.add("hidden");
    }
  });
  function updateCharCount() {
    let text = giftMessageInput.value;
    if (text.length > 200) {
      text = text.slice(0, 200);
      giftMessageInput.value = text;
    }
    charCounter.textContent = `( ${text.length} / 200 )`;
  }
  updateCharCount();
  giftMessageInput.addEventListener("input", updateCharCount);
});

// class to redirect to a tab for external links
const links = document.querySelectorAll("a.menu-redirect");

links.forEach((link) => {
  if (link.hostname !== window.location.hostname) {
    link.target = "_blank";
    link.rel = "noreferrer noopener";
  }
});

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll('modal-opener[data-modal="#CountrySelectorModal"]')
    .forEach((opener) => {
      opener.addEventListener("click", () => {
        const context = opener.getAttribute("data-context");

        const isFromPreferences = context === "preferences";

        setCountrySelectorTitle(isFromPreferences);

        const countrySelectorError = document.getElementById(
          "countrySelectorError",
        );
        if (countrySelectorError) {
          countrySelectorError.classList.add("hidden");
          document
            .querySelector(".country-search")
            ?.classList.remove("has-error");
          document
            .getElementById("countryResults")
            ?.classList.remove("has-error");
        }
      });
    });
});

// Mail Subscription
// document.addEventListener("DOMContentLoaded", () => {
//   const form = document.getElementById("email-preferences-form");
//   const tagsInput = document.getElementById("contact-tags-input");

//   const brandMappings = [
//     {
//       name: "contact[note][brand_abercrombie_fitch]",
//       tag: "brand:abercrombie_fitch",
//     },
//     {
//       name: "contact[note][brand_abercrombie_kids]",
//       tag: "brand:abercrombie_kids",
//     },
//     { name: "contact[note][brand_hollister]", tag: "brand:hollister" },
//   ];

//   if (form) {
//     form.addEventListener("submit", (event) => {
//       let tags = [];

//       brandMappings.forEach((mapping) => {
//         const checkbox = form.querySelector(`input[name="${mapping.name}"]`);

//         if (checkbox && checkbox.checked) {
//           tags.push(mapping.tag);
//         }
//       });

//       if (tagsInput) {
//         tagsInput.value = tags.join(",");
//       }
//     });
//   }
// });

//Language Fallback
document.addEventListener("DOMContentLoaded", () => {
  const defaultLocale = "en";
  const supportedLocales = ["en", "id"];
  const pathParts = window.location.pathname.split("/");
  let currentLocale = defaultLocale;
  if (supportedLocales.includes(pathParts[1])) {
    currentLocale = pathParts[1];
  }

  localStorage.setItem("selectedLocale", currentLocale);

  document.querySelectorAll('a[href^="/"]').forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    const parts = url.pathname.split("/");

    if (supportedLocales.includes(parts[1])) {
      parts.splice(1, 1);
    }

    if (currentLocale !== defaultLocale) {
      parts.splice(1, 0, currentLocale);
    }

    url.pathname = parts.join("/");
    link.href = url.toString();
  });

  document.querySelectorAll("[data-locale]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const selected = btn.dataset.locale;
      localStorage.setItem("selectedLocale", selected);

      const parts = window.location.pathname.split("/");
      if (supportedLocales.includes(parts[1])) parts.splice(1, 1);
      if (selected !== defaultLocale) parts.splice(1, 0, selected);

      window.location.href = parts.join("/") + window.location.search;
    });
  });
});

document.querySelectorAll(".login-redirect").forEach((element) => {
  element.addEventListener("click", (event) => {
    const redirectAfterLogin =
      element.getAttribute("data-redirect") || element.getAttribute("href");
    if (redirectAfterLogin) {
      event.preventDefault(); // Prevent default navigation for anchors

      sessionStorage.setItem("redirect_after_login", redirectAfterLogin);

      if (element.tagName.toLowerCase() === "a") {
        window.location.href = redirectAfterLogin;
      }
    }
  });
});

if (window.location.pathname === "/account/login") {
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get("order");
  const accountId = urlParams.get("account");
  const returnUrl = urlParams.get("return_url");
  let newRedirectUrl;

  if (orderId) {
    newRedirectUrl = `/account/orders/${orderId}`;
    sessionStorage.setItem("redirect_after_login", newRedirectUrl);
  } else if (accountId) {
    newRedirectUrl = `/pages/account`;
    sessionStorage.setItem("redirect_after_login", newRedirectUrl);
  } else if (returnUrl) {
    newRedirectUrl = returnUrl;
    sessionStorage.setItem("redirect_after_login", newRedirectUrl);
  } else if (!sessionStorage.getItem("redirect_after_login")) {
    newRedirectUrl = "/";
    sessionStorage.setItem("redirect_after_login", newRedirectUrl);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(".my-chat-wrapper");
  if (!container) return;

  function moveBotDiv(botDiv) {
    if (!container.contains(botDiv)) {
      container.appendChild(botDiv);
      console.log("Yellow.ai bot moved into container");
    }
  }

  // Check if the bot is already injected
  const existingBot = document.getElementById("ymPluginDivContainerInitial");
  if (existingBot) {
    moveBotDiv(existingBot);
  }

  // Watch for future injections
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.id === "ymPluginDivContainerInitial") {
          moveBotDiv(node);
        }
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

function setYmlFrameHeight() {
  const iframe = document.getElementById("#ymIframe");
  if (iframe) {
    iframe.style.height = "500px";
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const logoutBtn = document.getElementById("logout-btn");

  if (logoutBtn) {
    logoutBtn.addEventListener("click", (e) => {
      e.preventDefault();
      if (getCookie("cart")) deleteCookie("cart");
      localStorage.removeItem("myWishlist");
      localStorage.removeItem("savedItems");
      localStorage.removeItem("wishlistSynced");
      localStorage.removeItem("saveForLaterSynced");
      window.location.href = logoutBtn?.getAttribute("href");
    });
  }
});

function mergeWishlists(local = [], remote = []) {
  const map = new Map();

  const add = (item) => {
    if (!item) return;
    // Use handle + color as key, fallback to variantId if handle is missing
    const color = item.variantColor || item.option1 || "";
    const key = item.handle
      ? `${item.handle}:${color}`
      : item.variantId || item.id;

    if (key && !map.has(key)) {
      map.set(key, item);
    }
  };

  // Add remote first (source of truth), then local for any offline additions.
  remote.forEach(add);
  local.forEach(add);

  return Array.from(map.values());
}



function mergeSaveForLater(local = [], remote = []) {
  const allItems = [...local, ...remote];
  const seen = new Set();

  return allItems.filter((item) => {
    const id = item.savedId
      ? String(item.savedId)
      : String(item?.variantId || item?.variant_id || "");

    if (id && !seen.has(id)) {
      seen.add(id);
      return true;
    }
    return false;
  });
}

function waitForMetafields(keys = [], maxWait = 2000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    function check() {
      const metafields = window.customer?.metafields;

      if (metafields && keys.every((key) => metafields[key] !== undefined)) {
        resolve();
      } else if (Date.now() - start > maxWait) {
        reject(
          new Error(`Metafields [${keys.join(", ")}] did not load in time`),
        );
      } else {
        setTimeout(check, 200);
      }
    }

    if (!window.customer?.id) {
      reject(new Error("No logged-in customer"));
      return;
    }

    check();
  });
}

function filterExpiredWishlist(wishlist) {
  const now = Date.now();
  const expiryMs = 60 * 24 * 60 * 60 * 1000; // 60 days
  if (!Array.isArray(wishlist)) return [];
  return wishlist.filter((item) => {
    if (!item || !item.addedAt) return true; // keep if no timestamp
    return now - item.addedAt < expiryMs;
  });
}

async function syncWishlist() {
  const wishlist = JSON.parse(localStorage.getItem("myWishlist") || "[]");
  if (!window.customer?.id) return;

  try {
    const response = await window.mapclubFetch("customer-wishlist-data", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customerId: window.customer.id,
        wishlistData: wishlist,
      }),
    });

    if (!response.ok) {
      throw new Error("Sync failed");
    }

    const data = await response.json();
    // Assuming endpoint returns { success: true, updatedWishlist: [...] } – update your endpoint if needed
    if (data.success && data.updatedWishlist) {
      localStorage.setItem("myWishlist", JSON.stringify(data.updatedWishlist));
      // Publish event for UI refresh
      publish(PUB_SUB_EVENTS.wishlistUpdate, {
        wishlist: data.updatedWishlist,
      });
    }
  } catch (err) {
    console.error("Wishlist sync failed:", err);
  }
}

async function handleWishlistSyncFlow() {
  let wishlistSynced = localStorage.getItem("wishlistSynced");
  if (!window?.customer?.id && wishlistSynced) {
    localStorage.removeItem("wishlistSynced");
  }
  if (!window.customer?.id) {
    return;
  }

  try {
    try {
      await waitForMetafields(["wishlist"]);
    } catch (e) { }

    const localWishlist = JSON.parse(
      localStorage.getItem("myWishlist") || "[]",
    );

    let metafieldWishlist = [];
    const remoteData = window.customer?.metafields?.wishlist;

    if (remoteData) {
      if (typeof remoteData === "string") {
        try {
          metafieldWishlist = JSON.parse(remoteData);
        } catch (e) {
          console.error("Failed to parse remote wishlist JSON:", e);
        }
      } else if (Array.isArray(remoteData)) {
        metafieldWishlist = remoteData;
      } else if (typeof remoteData === "object") {
        metafieldWishlist = [remoteData];
      }
    }

    const isFirstSync = !localStorage.getItem("wishlistSynced");
    let mergedWishlist = [];
    if (isFirstSync) {
      // First sync after login: MERGE local guest items with remote account items
      mergedWishlist = mergeWishlists(localWishlist, metafieldWishlist);
    } else {
      // Subsequent syncs: Trust metafield (remote) as the prioritized source
      if (remoteData !== undefined && remoteData !== null) {
        mergedWishlist = metafieldWishlist;
      } else {
        mergedWishlist = localWishlist;
      }
    }

    if (isFirstSync) {
      const originalLength = mergedWishlist.length;
      mergedWishlist = filterExpiredWishlist(mergedWishlist);

      if (mergedWishlist.length > 0) {
        localStorage.setItem("myWishlist", JSON.stringify(mergedWishlist));
        await syncWishlist();
        localStorage.setItem("wishlistSynced", "true");
      } else {
        if (originalLength > 0) {
          localStorage.setItem("myWishlist", "[]");
          await syncWishlist();
        }
        localStorage.setItem("wishlistSynced", "true");
      }
    } else {

      const localStr = JSON.stringify(localWishlist);
      const mergedStr = JSON.stringify(mergedWishlist);

      if (localStr !== mergedStr) {
        localStorage.setItem("myWishlist", mergedStr);
        await syncWishlist();
      }
    }
  } catch (error) {
    console.error("handleWishlistSyncFlow error:", error);
  }
}


document.addEventListener("DOMContentLoaded", handleWishlistSyncFlow);

function handleTooltipClick(element) {
  // Get all tooltips
  const allTooltips = document.querySelectorAll(".tooltip-text");

  // Remove 'tooltip-visible' from all tooltips
  allTooltips.forEach((tooltip) => {
    if (tooltip !== element.querySelector(".tooltip-text")) {
      tooltip.classList.remove("tooltip-visible");
    }
  });
  // Example: toggle tooltip text visibility
  const text = element.querySelector(".tooltip-text");
  if (!text) return;
  text.classList.toggle("tooltip-visible");
}

// Detect click outside tooltips
document.addEventListener("click", (event) => {
  const tooltipElements = document.querySelectorAll(".tooltip-text");

  tooltipElements.forEach((tooltip) => {
    // Check if click is outside tooltip and its parent element
    if (
      !tooltip.contains(event.target) &&
      !tooltip.parentElement.contains(event.target)
    ) {
      tooltip.classList.remove("tooltip-visible");
    }
  });
});

//Plp +N color swatch
// document.addEventListener('DOMContentLoaded', function() {
//   function updateSwatches() {
//     const isSmallScreen = window.innerWidth < 1024;
//     const limit = isSmallScreen ? 4 : 6;

//     document.querySelectorAll('.spf-product__swatchs').forEach(function(swatchList) {
//       const swatches = swatchList.querySelectorAll('.spf-product__swatch');
//       const totalSwatches = swatches.length;
//       const hiddenCount = totalSwatches > limit ? totalSwatches - limit : 0;

//       swatches.forEach((swatch, index) => {
//         swatch.style.display = (index + 1 > limit) ? 'none' : 'inline-flex';
//       });

//       let moreIndicator = swatchList.querySelector('.swatch_rest_count');
//       if (hiddenCount > 0) {
//         if (!moreIndicator) {
//           moreIndicator = document.createElement('span');
//           moreIndicator.classList.add('swatch_rest_count', 'ml-2', 'text-[1.3rem]');
//           swatchList.appendChild(moreIndicator);
//         }
//         moreIndicator.textContent = `+${hiddenCount}`;
//         // moreIndicator.style.display = 'inline-flex';

//         if (isSmallScreen) {
//           moreIndicator.style.cursor = 'pointer';
//           moreIndicator.onclick = function() {
//             swatches.forEach(swatch => {
//               // swatch.style.display = 'inline-flex';
//             });
//             moreIndicator.style.display = 'none';
//           };
//         } else {
//           moreIndicator.onclick = null;
//           moreIndicator.style.cursor = 'default';
//         }
//       } else if (moreIndicator) {
//         moreIndicator.style.display = 'none';
//       }
//     });
//   }

//   updateSwatches();

//   window.addEventListener('resize', updateSwatches);
// });

// Store Locator - Dynamic Injections
(function () {
  function injectStoreLocatorAdditions() {
    // 1. Inject "View Map" link in header
    const storeHeader = document.querySelector(
      ".store-locator-view-all-stores-header",
    );
    if (storeHeader && !storeHeader.querySelector(".store-map-link")) {
      const mapLink = document.createElement("a");
      mapLink.href = "https://www.hollisterco.id/pages/store-map-locator";
      mapLink.textContent = "Store Locator";
      mapLink.className = "store-map-link";
      mapLink.style.textDecoration = "underline";
      mapLink.style.color = "#233B4D ";
      mapLink.style.transition = "color 0.3s ease";

      // Add hover effect
      mapLink.addEventListener("mouseenter", () => {
        mapLink.style.color = "#233B4D ";
      });
      mapLink.addEventListener("mouseleave", () => {
        mapLink.style.color = "#233B4D ";
      });

      storeHeader.style.display = "flex";
      storeHeader.style.justifyContent = "space-between";
      storeHeader.style.alignItems = "center";
      storeHeader.appendChild(mapLink);
    }

    // 2. Inject logo, right arrow and reformat location rows
    const rows = document.querySelectorAll(
      ".store-locator-view-all-stores-location-row",
    );
    rows.forEach((row) => {
      const nameEl = row.querySelector(
        ".store-locator-view-all-stores-location-name",
      );
      const addressEl = row.querySelector(
        ".store-locator-view-all-stores-location-address",
      );

      if (nameEl && addressEl && !row.dataset.formatted) {
        // Concatenate: "Address, Name" (e.g. Grand Indonesia, Jl. M.H. Thamrin)
        const addressText = addressEl.textContent.trim();
        const nameText = nameEl.textContent.trim();

        if (addressText && nameText) {
          nameEl.textContent = `${addressText}, ${nameText}`;
        }
        // Hide original address element
        addressEl.style.display = "none";

        // Mark as formatted
        row.dataset.formatted = "true";
      }

      // Prepend Logo
      if (!row.querySelector(".store-row-logo")) {
        const logo = document.createElement("span");
        logo.className = "store-row-logo";
        logo.textContent = "";
        logo.style.fontFamily = "Responsive Icons";
        logo.style.fontSize = "2rem";
        logo.style.color = "#233B4D ";
        logo.style.flexShrink = "0";
        row.prepend(logo);
      }

      // Append Arrow
      if (!row.querySelector(".store-row-arrow")) {
        const arrow = document.createElement("span");
        arrow.className = "store-row-arrow";
        arrow.textContent = "";
        arrow.style.fontFamily = "Responsive Icons";
        arrow.style.fontSize = "1.5rem";
        arrow.style.color = "#5e5e5e";
        arrow.style.pointerEvents = "none";
        arrow.style.flexShrink = "0";

        row.appendChild(arrow);
      }

      // Row alignment
      row.style.display = "flex";
      row.style.justifyContent = "space-between";
      row.style.alignItems = "center";
      row.style.gap = "15px";

      if (nameEl) {
        nameEl.style.flexGrow = "1";
      }
    });
  }

  // Try immediately on DOMContentLoaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", injectStoreLocatorAdditions);
  } else {
    injectStoreLocatorAdditions();
  }

  // Watch for elements to appear (as they are loaded by an external app script)
  const observer = new MutationObserver(function (mutations) {
    injectStoreLocatorAdditions();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });


  setTimeout(() => {
    if (
      !document.querySelector(".store-locator-view-all-stores-location-row")
    ) {
      observer.disconnect();
    }
  }, 30000);
})();

document.addEventListener("click", (e) => {
  const opener = e.target.closest("modal-opener");
  if (!opener) return;

  const modalSelector = opener.dataset.modal;
  const mediaId = opener.dataset.thumbId;

  if (!modalSelector || !mediaId) return;
  if (!modalSelector.startsWith("#ProductModal-")) return;
  const modal = document.querySelector(modalSelector);
  if (!modal) return;
  const sectionId = modalSelector.replace("#ProductModal-", "");

  modal.dataset.mediaId = `Slide-${sectionId}-${mediaId}`;
  modal.dispatchEvent(new Event("show"));
});

document.addEventListener("DOMContentLoaded", () => {
  // Find ALL product sections and init
  document
    .querySelectorAll("product-info[data-section]")
    .forEach((productInfo) => {
      const sectionId = productInfo.dataset.section;
      initMobileSwiper(sectionId, { source: "global:DOMContentLoaded" });
    });
});

async function syncSaveForLater() {
  if (!window.customer?.id) return;

  let savedItems = [];

  try {
    savedItems = JSON.parse(localStorage.getItem("savedItems")) || [];
  } catch {
    savedItems = [];
  }

  try {
    const response = await window.mapclubFetch("customer-save-later-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerId: window?.customer?.id,
        saveLaterData: savedItems,
      }),
    });

    if (!response.ok) throw new Error("Sync failed");

    const data = await response.json();

    if (data.success && data.updatedSavedItems) {
      localStorage.setItem(
        "savedItems",
        JSON.stringify(data.updatedSavedItems),
      );

      // Re-render saved tab if functions exist (only on cart page)
      if (typeof renderSavedItemsFromStorage === "function")
        renderSavedItemsFromStorage();
      if (typeof updateTabCounts === "function") updateTabCounts();
      if (typeof manageEmptyStates === "function") manageEmptyStates();
    }
  } catch (err) {
    console.error("Save for later sync failed:", err);
  }
}

async function handleSaveForLaterSyncFlow() {
  let synced = localStorage.getItem("saveForLaterSynced");

  if (!window?.customer?.id && synced) {
    localStorage.removeItem("saveForLaterSynced");
    return;
  }

  if (!window.customer?.id) return;



  try {
    const localSaved = JSON.parse(localStorage.getItem("savedItems") || "[]");

    let metafieldSaved = [];
    const rawMeta = window.customer?.metafields?.save_later;

    if (rawMeta) {
      metafieldSaved =
        typeof rawMeta === "string" ? JSON.parse(rawMeta) : rawMeta;
      console.log("Metafield saved items:", metafieldSaved);
    }

    const isFirstSync = !localStorage.getItem("saveForLaterSynced");

    if (isFirstSync) {
      // First sync: Merge guest items with server items
      const merged = mergeSaveForLater(localSaved, metafieldSaved);

      if (merged.length > 0) {
        localStorage.setItem("savedItems", JSON.stringify(merged));
        await syncSaveForLater(); // Sync merged data to server
      } else if (metafieldSaved && Array.isArray(metafieldSaved)) {
        localStorage.setItem("savedItems", JSON.stringify(metafieldSaved));
      } else {
        localStorage.setItem("savedItems", "[]");
      }

      // Trigger UI refreshes if on cart page
      if (typeof window.renderSavedItemsFromStorage === "function") {
        window.renderSavedItemsFromStorage();
      } else if (typeof window.updateTabCounts === "function") {
        window.updateTabCounts();
      }

      localStorage.setItem("saveForLaterSynced", "true");
    } else {
      // After session begins: Metafield is source of truth (overwrite)
      if (
        metafieldSaved &&
        Array.isArray(metafieldSaved) &&
        metafieldSaved.length > 0
      ) {
        localStorage.setItem("savedItems", JSON.stringify(metafieldSaved));
      } else {
        // If metafield is empty, clear localStorage
        localStorage.setItem("savedItems", "[]");
      }

      // Trigger UI refreshes if on cart page
      if (typeof window.renderSavedItemsFromStorage === "function") {
        window.renderSavedItemsFromStorage();
      } else if (typeof window.updateTabCounts === "function") {
        window.updateTabCounts();
      }
    }
  } catch (err) {
    console.warn("Save for later sync flow status:", err.message);
  }
}

document.addEventListener("DOMContentLoaded", handleSaveForLaterSyncFlow);

window.hideGloboFilters = function (labels, breakpoint = 1024) {
  const performHide = () => {
    const isTargetSize = window.innerWidth <= breakpoint;

    // Only hide on mobile/tablet — never touch desktop display
    if (!isTargetSize) return;

    // 1. Hide the specific filter blocks based on labels/types (mobile only)
    if (labels && Array.isArray(labels)) {
      labels.forEach((label) => {
        const blocks = document.querySelectorAll(
          `.gf-option-block[data-filter-type='${label}'], .gf-option-block-${label.toLowerCase()}`,
        );
        blocks.forEach((block) => {
          block.style.setProperty("display", "none", "important");
        });
      });
    }

    // 2. Hide the "Selected Items" block (mobile only)
    const selectedItems = document.querySelectorAll(
      ".gf-block-content.globo-selected-items, .globo-selected-items-wrapper.visible-xs",
    );
    selectedItems.forEach((item) => {
      item.style.setProperty("display", "none", "important");
    });
  };

  // Run immediately
  performHide();

  // Only use resize listener — no aggressive MutationObserver or polling
  // to prevent conflicts with other scripts that manage the Collection filter
  window.addEventListener("resize", performHide);

  // Listen for Globo re-renders to re-apply mobile hide
  window.addEventListener("globoFilterRenderCompleted", () => {
    setTimeout(performHide, 100);
  });

  return {
    observer: null,
    stop: () => {
      window.removeEventListener("resize", performHide);
    },
  };
};

function handleClearAll() {
  const elem = document.querySelector(".collection-result-count");
  if (elem) {
    elem.style.visibility = "hidden";
    elem.style.opacity = "0";
  }
  clearAllFilter();
}

async function clearSaveForLater() {
  const savedItems = [];

  try {
    const response = await window.mapclubFetch("customer-save-later-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerId: window?.customer?.id,
        saveLaterData: savedItems,
      }),
    });

    if (!response.ok) throw new Error("Clear sync failed");
  } catch (err) {
    console.error("Clear save for later failed:", err);
  }
}


function handleNewsletterHashLink() {
  if (window.location.hash === "#newsletter-signup") {
    const newsletterInput = document.querySelector(
      '.newsletter-form__field-wrapper input[type="email"]',
    );
    if (newsletterInput) {
      setTimeout(() => {
        newsletterInput.scrollIntoView({ behavior: "smooth", block: "center" });
        newsletterInput.focus();
      }, 500);
    }
  }
}
window.addEventListener("hashchange", handleNewsletterHashLink);
document.addEventListener("DOMContentLoaded", handleNewsletterHashLink);

//variant listener and add to bag btn consistency
document.addEventListener('variant:change', function (event) {
  const variant = event.detail.variant;
  if (!variant) return;
  document.querySelectorAll('.product-variant-id').forEach(input => {
    input.value = variant.id;
    input.disabled = false;
  });
  document.querySelectorAll('.product-form__submit').forEach(button => {
    if (variant.available) {
      button.disabled = false;
    } else {
      button.disabled = true;
    }
  });
});
// Track if user changed variant
let userChangedVariant = false;
document.addEventListener('variant:change', function () {
  userChangedVariant = true;
});
document.addEventListener('change', function (e) {
  if (!e.target.closest('variant-selects')) return;
  setTimeout(() => {
    const variantId = document.querySelector('.product-variant-id')?.value;
    if (!variantId) return;
    document.querySelectorAll('.product-variant-id').forEach(input => {
      input.value = variantId;
    });
  }, 50);
});
//end
